export type Article = {
  id: string;
  title: string;
  excerpt: string;
  cover: string;
  category: "film-info" | "related-info";
  type: string;
  date: string;
  views: number;
  readMin: number;
  tags: string[];
  actors: string[];
  content: string[];
};

export type Actor = {
  id: string;
  name: string;
  role: string;
  avatar: string;
  tagline: string;
  bio: string;
  facts: { label: string; value: string }[];
  films: { id: string; title: string; cover: string; year: string }[];
};

export type GuideGroup = {
  key: string;
  label: string;
  intro: string;
  items: { title: string; desc: string; articleId: string }[];
};

export const siteInfo = {
  brand: "妈妈的职业电影",
  slogan: "读懂一位母亲的双重人生",
  intro:
    "妈妈的职业电影是一个专注《妈妈的职业电影》影片内容的中文影视门户。我们整理剧情脉络、人物关系、演员阵容与观看建议，帮助观众在观影前后快速抓住故事的核心，也分享关于母亲题材影片的更多思考。",
  stats: [
    { label: "剧情解析文章", value: "48+" },
    { label: "演员与角色档案", value: "26" },
    { label: "观影指南专题", value: "12" },
    { label: "累计阅读量", value: "120万+" },
  ],
};

export const heroSlides = [
  {
    id: "art-01",
    kicker: "剧情解析 · 深度长文",
    title: "《妈妈的职业电影》剧情全解析",
    desc: "一位母亲在家庭与职场之间来回穿行的二十年。我们从开篇的厨房镜头讲起，拆解每一处伏笔与呼应。",
    image:
      "https://readdy.ai/api/search-image?query=Cinematic%20warm-toned%20film%20still%2C%20a%20middle-aged%20woman%20standing%20in%20a%20cozy%20kitchen%20bathed%20in%20soft%20afternoon%20light%2C%20muted%20blue-gray%20and%20warm%20orange%20palette%2C%20shallow%20depth%20of%20field%2C%20editorial%20movie%20poster%20aesthetic%2C%20no%20text%2C%20atmospheric%20and%20emotional&width=1600&height=900&seq=momfilmhero01&orientation=landscape",
    link: "/detail/art-01",
  },
  {
    id: "art-03",
    kicker: "结局解读 · 读者热议",
    title: "那封没寄出的信，到底意味着什么",
    desc: "影片结尾没有一句台词，却让人久久说不出话。我们整理了三种主流解读，以及导演留下的线索。",
    image:
      "https://readdy.ai/api/search-image?query=Cinematic%20close-up%20portrait%20of%20a%20determined%20middle-aged%20woman%20looking%20out%20a%20rain-streaked%20window%20at%20dusk%2C%20moody%20blue-gray%20tones%20with%20a%20warm%20amber%20highlight%2C%20soft%20cinematic%20lighting%2C%20film%20grain%2C%20editorial%20poster%20aesthetic%2C%20no%20text%2C%20emotional%20atmosphere&width=1600&height=900&seq=momfilmhero02&orientation=landscape",
    link: "/detail/art-03",
  },
  {
    id: "art-14",
    kicker: "相关资讯 · 拍摄地巡礼",
    title: "跟着镜头走进影片里的街巷",
    desc: "那条反复出现的梧桐老街、街角的小面馆、黄昏的天桥，都是剧组用真实场景一点点搭出来的。",
    image:
      "https://readdy.ai/api/search-image?query=Wide%20cinematic%20shot%20of%20a%20quiet%20city%20street%20lined%20with%20plane%20trees%20at%20golden%20hour%2C%20warm%20orange%20glow%20and%20cool%20blue-gray%20shadows%2C%20blurred%20bokeh%20lights%2C%20film%20still%20look%2C%20no%20text%2C%20hopeful%20and%20moving%20mood&width=1600&height=900&seq=momfilmhero03&orientation=landscape",
    link: "/detail/art-14",
  },
];

export const categoryMeta = {
  "film-info": {
    path: "/category/film-info",
    name: "电影资讯",
    desc: "《妈妈的职业电影》的剧情脉络、人物关系、主题解读与深度解析，帮你把故事看透。",
    image:
      "https://readdy.ai/api/search-image?query=Stack%20of%20vintage%20film%20reels%20and%20a%20cinema%20clapperboard%20on%20a%20wooden%20table%2C%20warm%20soft%20daylight%2C%20muted%20blue-gray%20and%20orange%20palette%2C%20editorial%20photography%2C%20no%20text%2C%20clean%20minimal%20composition&width=900&height=640&seq=momfilmcatfilm&orientation=landscape",
  },
  "related-info": {
    path: "/category/related-info",
    name: "相关影视资讯",
    desc: "聚焦影片幕后、演员动态、配乐创作与同类影片推荐，看完整部电影还想了解更多。",
    image:
      "https://readdy.ai/api/search-image?query=Backstage%20film%20set%20with%20soft%20focus%20lighting%20equipment%20and%20an%20empty%20director%20chair%2C%20warm%20and%20cool%20cinematic%20tones%2C%20editorial%20photography%2C%20no%20text%2C%20clean%20minimal%20composition&width=900&height=640&seq=momfilmcatrelated&orientation=landscape",
  },
} as const;

export const articles: Article[] = [
  {
    id: "art-01",
    title: "《妈妈的职业电影》剧情全解析：一位母亲的双重身份",
    excerpt:
      "从清晨的厨房到深夜的办公室，影片用二十年时间讲述苏明娟如何在母亲与职业人之间来回穿行。这是全片最完整的一条剧情线。",
    cover:
      "https://readdy.ai/api/search-image?query=Cinematic%20warm-toned%20film%20still%20of%20a%20middle-aged%20woman%20in%20a%20sunlit%20kitchen%20preparing%20breakfast%2C%20muted%20blue-gray%20and%20orange%20palette%2C%20shallow%20depth%20of%20field%2C%20editorial%20photography%2C%20no%20text%2C%20warm%20emotional%20mood&width=1000&height=640&seq=momfilmart01&orientation=landscape",
    category: "film-info",
    type: "剧情介绍",
    date: "2026-09-28",
    views: 48210,
    readMin: 9,
    tags: ["剧情解析", "主线梳理"],
    actors: ["lin-wanqiu", "gu-qinghe"],
    content: [
      "影片从一个再普通不过的清晨开始。苏明娟在厨房里煎蛋、热牛奶，顺手把女儿的校服叠好放进门边。镜头没有一句多余的台词，却已经把这个人物的全部底色交代清楚——她习惯先照顾别人，再考虑自己。",
      "故事的时间线横跨二十年。前半段，苏明娟是一名刚复出职场的护理师，她在医院值完夜班还要赶回家做早饭；后半段，女儿苏念长大成人，开始用另一种眼光重新审视母亲当年的选择。两条时间线在影片中段交汇，构成了整部电影最有力的结构。",
      "值得注意的是，导演始终没有把苏明娟塑造成一个完美的母亲。她会因为加班错过女儿的家长会，也会在深夜独自落泪。正是这些不完美的瞬间，让角色真正立了起来。",
      "影片最后二十分钟，苏明娟站在天桥上看着来来往往的人流，终于说出那句积压了二十年的话。理解这句话，也就理解了整部电影的全部重量。",
    ],
  },
  {
    id: "art-02",
    title: "剧情主线梳理：从家庭到职场的每一次抉择",
    excerpt:
      "每一次选择都不是非黑即白。我们把影片中苏明娟的五个关键抉择逐一拆开，看看它们如何把剧情推向后来的转折。",
    cover:
      "https://readdy.ai/api/search-image?query=Cinematic%20film%20still%20of%20a%20hospital%20corridor%20at%20night%20with%20soft%20fluorescent%20light%2C%20a%20lone%20figure%20in%20nurse%20uniform%20walking%20away%2C%20cool%20blue-gray%20tones%20with%20warm%20accents%2C%20editorial%20photography%2C%20no%20text%2C%20quiet%20mood&width=1000&height=640&seq=momfilmart02&orientation=landscape",
    category: "film-info",
    type: "剧情介绍",
    date: "2026-09-25",
    views: 33120,
    readMin: 8,
    tags: ["剧情解析", "抉择"],
    actors: ["lin-wanqiu", "chen-ziang"],
    content: [
      "第一个抉择发生在影片开场不久：复出职场，还是继续留在家里。苏明娟选择了前者，而这一个决定，牵动了后面整整二十年的故事。",
      "第二个抉择关于女儿的教育。当苏念获得外地求学的机会，苏明娟选择放手，却在站台上站到最后一班车离去。这个镜头几乎是全片最短、也最容易被忽略的一次告别。",
      "第三个抉择出现在中段，面对职业晋升与家庭责任的两难，她把机会让给了同事。很多观众认为这是影片的转折点，因为她第一次主动为别人让步。",
      "第四、第五个抉择层层递进，最终把人物推向结局。梳理清楚这五个节点，整条主线就清晰了。",
    ],
  },
  {
    id: "art-03",
    title: "结局解读：那封没寄出的信意味着什么",
    excerpt:
      "影片最后，抽屉里那封始终没有寄出的信成为最大的悬念。我们整理了三种主流解读，以及藏在镜头里的关键线索。",
    cover:
      "https://readdy.ai/api/search-image?query=Cinematic%20film%20still%20of%20an%20old%20wooden%20drawer%20slightly%20open%20with%20a%20folded%20letter%20inside%2C%20warm%20lamp%20light%20and%20deep%20blue-gray%20shadows%2C%20editorial%20photography%2C%20no%20text%2C%20mysterious%20and%20tender%20mood&width=1000&height=640&seq=momfilmart03&orientation=landscape",
    category: "film-info",
    type: "结局解读",
    date: "2026-09-22",
    views: 41560,
    readMin: 7,
    tags: ["结局", "深度解析"],
    actors: ["lin-wanqiu", "gu-qinghe"],
    content: [
      "结局没有正面交代那封信的内容，只给了一个缓缓合上抽屉的动作。这个留白恰恰是全片最高明的一笔。",
      "第一种解读把信理解为苏明娟对自己的道歉，她终于允许自己承认这些年的疲惫。第二种解读认为信是写给女儿的，只是她始终没有勇气寄出。第三种解读则更克制：信写了什么并不重要，重要的是她终于坐下来写下了它。",
      "如果留意结尾的光线，会发现天桥上的镜头第一次没有使用冷色调，而是被夕阳完全包裹。导演用光线给出了自己的答案。",
    ],
  },
  {
    id: "art-04",
    title: "人物关系图谱：母亲、女儿与那个被隐瞒的秘密",
    excerpt:
      "片中有三条人物关系线彼此缠绕。我们把每一段关系的关键场景和台词都标注出来，帮你理清这张复杂的关系网。",
    cover:
      "https://readdy.ai/api/search-image?query=Cinematic%20film%20still%20of%20a%20mother%20and%20her%20grown%20daughter%20sitting%20at%20a%20table%20facing%20each%20other%20under%20warm%20window%20light%2C%20muted%20orange%20and%20blue-gray%20palette%2C%20editorial%20photography%2C%20no%20text%2C%20intimate%20and%20tender%20mood&width=1000&height=640&seq=momfilmart04&orientation=landscape",
    category: "film-info",
    type: "人物解析",
    date: "2026-09-18",
    views: 28740,
    readMin: 8,
    tags: ["人物关系", "解析"],
    actors: ["lin-wanqiu", "gu-qinghe", "chen-ziang"],
    content: [
      "母女关系是全片的骨架。苏明娟习惯用行动表达爱，而苏念习惯用语言追问答案，两代人表达方式的不同，构成了大量戏剧张力。",
      "周叙的出现打破了原本的平衡。他既像旁观者，又像某种意义上的见证人，他看穿了苏明娟没能说出口的部分。",
      "那个被隐瞒多年的秘密，其实是全片所有人物关系的交汇点。当它在后半段被揭开时，重新定义了此前所有的相处细节。",
    ],
  },
  {
    id: "art-05",
    title: "开篇十分钟全解析：藏在日常里的三处伏笔",
    excerpt:
      "很多人以为开篇只是普通的家庭日常，其实导演已经悄悄埋下了三处关键伏笔。看完这篇，你会想立刻重看一遍开头。",
    cover:
      "https://readdy.ai/api/search-image?query=Cinematic%20film%20still%20of%20a%20breakfast%20table%20with%20simple%20dishes%20and%20a%20school%20uniform%20folded%20neatly%2C%20soft%20morning%20light%2C%20warm%20orange%20and%20muted%20blue-gray%20palette%2C%20editorial%20photography%2C%20no%20text%2C%20cozy%20mood&width=1000&height=640&seq=momfilmart05&orientation=landscape",
    category: "film-info",
    type: "细节解读",
    date: "2026-09-14",
    views: 22190,
    readMin: 6,
    tags: ["开篇", "伏笔"],
    actors: ["lin-wanqiu"],
    content: [
      "第一处伏笔藏在墙上的日历里。那些被红笔圈出的日期，实际上是苏明娟值夜班的日子，与后面的剧情完全对得上。",
      "第二处伏笔是那件永远叠得整整齐齐的校服。它代表着苏明娟对秩序和掌控感的坚持，也暗示了她内心的不安。",
      "第三处伏笔出现在结尾前的一句闲谈里，看似随口一提，实则与结局遥相呼应。",
    ],
  },
  {
    id: "art-06",
    title: "主题解读：职业与母职之间，她如何安放自己",
    excerpt:
      "这部电影真正想讨论的，不是“母亲该不该工作”，而是一个人在承担多重身份时，如何不把自己弄丢。",
    cover:
      "https://readdy.ai/api/search-image?query=Cinematic%20film%20still%20of%20a%20woman%20standing%20between%20a%20hospital%20doorway%20and%20a%20bright%20city%20street%2C%20split%20warm%20orange%20and%20cool%20blue-gray%20light%2C%20editorial%20photography%2C%20no%20text%2C%20reflective%20mood&width=1000&height=640&seq=momfilmart06&orientation=landscape",
    category: "film-info",
    type: "主题解读",
    date: "2026-09-10",
    views: 19430,
    readMin: 7,
    tags: ["主题", "深度解析"],
    actors: ["lin-wanqiu", "xu-yiran"],
    content: [
      "影片没有给出标准答案，而是把问题交还给观众。苏明娟既没有彻底回归家庭，也没有完全投入事业，她一直在寻找那个平衡点。",
      "片中反复出现的“门”是一个重要意象：推开是职场，关上就是家。她一生都在这两扇门之间来回。",
      "到最后，她终于学会在两扇门之间留一道缝，那道光既是妥协，也是和解。",
    ],
  },
  {
    id: "art-07",
    title: "经典台词盘点：那些让人瞬间破防的对白",
    excerpt:
      "有些台词只有短短一句，却让整个影院安静下来。我们整理了全片最值得回味的十二句对白。",
    cover:
      "https://readdy.ai/api/search-image?query=Cinematic%20film%20still%20of%20two%20people%20in%20quiet%20conversation%20in%20a%20dim%20room%20lit%20by%20a%20single%20warm%20lamp%2C%20deep%20blue-gray%20shadows%20with%20orange%20highlight%2C%20editorial%20photography%2C%20no%20text%2C%20emotional%20mood&width=1000&height=640&seq=momfilmart07&orientation=landscape",
    category: "film-info",
    type: "台词盘点",
    date: "2026-09-06",
    views: 35680,
    readMin: 5,
    tags: ["台词", "盘点"],
    actors: ["lin-wanqiu", "gu-qinghe"],
    content: [
      "“妈，你这辈子有没有为自己活过？”——苏念在餐桌上的这句追问，是全片最锋利的一次对话。",
      "“我不是不累，我只是不敢停。”——苏明娟的回答几乎击中了每一位观众。",
      "还有那句被反复引用的“门开着，你随时可以回来”，简短却承载了整部电影的温度。",
    ],
  },
  {
    id: "art-08",
    title: "剧情时间线：二十年跨度是怎么讲好一个故事的",
    excerpt:
      "两个时间线交叉推进，却丝毫不乱。这篇文章带你梳理影片的时间结构，看清导演的叙事野心。",
    cover:
      "https://readdy.ai/api/search-image?query=Cinematic%20film%20still%20showing%20a%20timeline%20of%20seasons%20through%20a%20window%2C%20autumn%20leaves%20and%20winter%20snow%20blurring%20into%20spring%2C%20warm%20orange%20and%20blue-gray%20palette%2C%20editorial%20photography%2C%20no%20text%2C%20nostalgic%20mood&width=1000&height=640&seq=momfilmart08&orientation=landscape",
    category: "film-info",
    type: "结构解析",
    date: "2026-09-02",
    views: 16250,
    readMin: 8,
    tags: ["时间线", "叙事"],
    actors: ["lin-wanqiu", "gu-qinghe"],
    content: [
      "影片使用了双线并行的结构：一条是当下的清醒，一条是回忆里的温柔。两条线在中段自然交汇。",
      "剪辑上大量使用了匹配剪接，比如厨房的煎蛋声切到医院的仪器声，暗示了两个身份的一体两面。",
      "理解这个结构，才能明白为什么结尾只有短短几分钟，却让人感觉走完了一生。",
    ],
  },
  {
    id: "art-09",
    title: "幕后花絮：影片中的厨房场景是怎么搭出来的",
    excerpt:
      "那个充满烟火气的厨房，其实是一比一还原搭建的布景。美术团队分享了其中的细节与巧思。",
    cover:
      "https://readdy.ai/api/search-image?query=Behind%20the%20scenes%20film%20set%20of%20a%20warm%20domestic%20kitchen%20with%20props%20and%20lighting%20stands%2C%20muted%20orange%20and%20blue-gray%20tones%2C%20editorial%20photography%2C%20no%20text%2C%20crafted%20atmosphere&width=1000&height=640&seq=momfilmart09&orientation=landscape",
    category: "related-info",
    type: "幕后花絮",
    date: "2026-09-27",
    views: 24870,
    readMin: 6,
    tags: ["幕后", "美术"],
    actors: ["shen-shubai"],
    content: [
      "美术团队花了六周时间搭建这个厨房。为了让光线更自然，他们特意把窗户朝向东侧，只为捕捉清晨那一小段柔和的光。",
      "橱柜上的每一件小物都有来历：缺角的碗、用了十年的锅、贴满便签的冰箱门，都来自真实的家庭旧物。",
      "正是这些不被镜头强调的细节，让观众相信这个地方真的有人住过。",
    ],
  },
  {
    id: "art-10",
    title: "配乐解析：主题曲如何为剧情加分",
    excerpt:
      "全片配乐克制到近乎沉默，却在最需要的时候精准落下。这篇文章带你听见旋律背后的情绪设计。",
    cover:
      "https://readdy.ai/api/search-image?query=Cinematic%20film%20still%20of%20a%20piano%20in%20a%20dim%20room%20with%20sheets%20of%20music%2C%20warm%20amber%20light%20and%20deep%20blue-gray%20shadows%2C%20editorial%20photography%2C%20no%20text%2C%20melancholic%20mood&width=1000&height=640&seq=momfilmart10&orientation=landscape",
    category: "related-info",
    type: "配乐",
    date: "2026-09-24",
    views: 17540,
    readMin: 6,
    tags: ["配乐", "主题曲"],
    actors: ["chen-ziang"],
    content: [
      "作曲家选择了极简的钢琴动机，全片只围绕同一段旋律做变化，这让情绪始终保持着一种克制的连贯。",
      "主题曲第一次完整出现是在母女分别的站台戏，那一刻之前，配乐一直刻意保持沉默。",
      "最动人之处在于结尾，旋律突然停在一个没有解决的和弦上，把留白留给观众自己去完成。",
    ],
  },
  {
    id: "art-11",
    title: "导演访谈：这部电影的创作初衷与灵感来源",
    excerpt:
      "“我只是想拍一个普通人的二十年。”导演在访谈中谈到了影片的灵感、选角与那些被删掉的镜头。",
    cover:
      "https://readdy.ai/api/search-image?query=Cinematic%20film%20still%20of%20an%20empty%20director%20chair%20and%20monitor%20on%20a%20quiet%20film%20set%2C%20warm%20orange%20accent%20light%20and%20cool%20blue-gray%20shadows%2C%20editorial%20photography%2C%20no%20text%2C%20contemplative%20mood&width=1000&height=640&seq=momfilmart11&orientation=landscape",
    category: "related-info",
    type: "访谈",
    date: "2026-09-20",
    views: 21860,
    readMin: 7,
    tags: ["导演", "访谈"],
    actors: ["lin-wanqiu"],
    content: [
      "导演说，影片的灵感来自自己母亲的一本旧记事本，里面密密麻麻记着家里的每一笔开销。",
      "选角时他几乎只看了一眼林晚秋的试镜片段就确定了人选，因为“她的沉默里有故事”。",
      "他还透露，原本有一场长达八分钟的争吵戏，最后被全部剪掉，只留下一个眼神。",
    ],
  },
  {
    id: "art-12",
    title: "演员动态：主演谈角色塑造的幕后过程",
    excerpt:
      "为了演好一个护理师，主演跟着真实的医护人员上了整整两周的夜班。她分享了这段“体验生活”的经历。",
    cover:
      "https://readdy.ai/api/search-image?query=Cinematic%20film%20still%20of%20an%20actress%20studying%20a%20script%20backstage%20under%20warm%20makeup%20mirror%20light%2C%20muted%20orange%20and%20blue-gray%20palette%2C%20editorial%20photography%2C%20no%20text%2C%20focused%20mood&width=1000&height=640&seq=momfilmart12&orientation=landscape",
    category: "related-info",
    type: "演员动态",
    date: "2026-09-16",
    views: 26310,
    readMin: 6,
    tags: ["演员", "幕后"],
    actors: ["lin-wanqiu", "gu-qinghe"],
    content: [
      "为了贴近角色，林晚秋在开拍前跟随医护人员体验了两周夜班，学会了最基础的护理动作。",
      "她坦言最难的一场戏不是哭戏，而是安静地给女儿整理行李，“那种克制比流泪更难演”。",
      "顾清和则分享了自己如何通过观察母亲的小动作，去理解角色的心理变化。",
    ],
  },
  {
    id: "art-13",
    title: "同类影片推荐：五部关于母亲的电影",
    excerpt:
      "看完《妈妈的职业电影》意犹未尽？这五部同样聚焦母亲题材的影片，或许能带给你新的触动。",
    cover:
      "https://readdy.ai/api/search-image?query=Cinematic%20film%20still%20of%20a%20stack%20of%20movie%20posters%20and%20a%20warm%20cup%20of%20tea%20on%20a%20cozy%20table%2C%20soft%20window%20light%2C%20muted%20orange%20and%20blue-gray%20palette%2C%20editorial%20photography%2C%20no%20text%2C%20welcoming%20mood&width=1000&height=640&seq=momfilmart13&orientation=landscape",
    category: "related-info",
    type: "片单推荐",
    date: "2026-09-12",
    views: 30240,
    readMin: 7,
    tags: ["片单", "推荐"],
    actors: ["lin-wanqiu", "shen-shubai"],
    content: [
      "第一部推荐聚焦跨代母女关系，用细腻的日常展现了两代人的和解。",
      "第二、第三部则从不同文化视角切入，探讨母亲身份与自我实现之间的张力。",
      "最后两部偏向温暖治愈，适合在看完本片后作为情绪的缓冲。每部我们都附上了推荐理由。",
    ],
  },
  {
    id: "art-14",
    title: "拍摄地巡礼：跟着镜头走进影片里的街巷",
    excerpt:
      "那条反复出现的梧桐老街、街角的小面馆、黄昏的天桥，都是剧组用真实场景一点点还原出来的。",
    cover:
      "https://readdy.ai/api/search-image?query=Cinematic%20film%20still%20of%20a%20quiet%20old%20street%20with%20plane%20trees%20and%20a%20small%20noodle%20shop%2C%20golden%20hour%20light%2C%20warm%20orange%20and%20cool%20blue-gray%20palette%2C%20editorial%20photography%2C%20no%20text%2C%20nostalgic%20mood&width=1000&height=640&seq=momfilmart14&orientation=landscape",
    category: "related-info",
    type: "拍摄地",
    date: "2026-09-08",
    views: 14980,
    readMin: 5,
    tags: ["拍摄地", "巡礼"],
    actors: ["chen-ziang"],
    content: [
      "影片大量使用了实景拍摄，那条老街其实位于城市的老城区，至今仍保留着原貌。",
      "街角的面馆是剧组临时租下的，老板本人也客串了一个没有台词的路人。",
      "黄昏的天桥戏只拍了两次就过了，导演说那是因为“黄昏本身就在演戏”。",
    ],
  },
];

export const actors: Actor[] = [
  {
    id: "lin-wanqiu",
    name: "林晚秋",
    role: "饰 苏明娟（母亲）",
    tagline: "把沉默演成台词",
    avatar:
      "https://readdy.ai/api/search-image?query=Cinematic%20studio%20portrait%20of%20a%20dignified%20middle-aged%20Chinese%20woman%20with%20soft%20short%20hair%2C%20calm%20expressive%20eyes%2C%20warm%20neutral%20background%20with%20muted%20orange%20and%20blue-gray%20tones%2C%20soft%20directional%20lighting%2C%20editorial%20photography%2C%20no%20text&width=520&height=650&seq=momfilmactor01&orientation=portrait",
    bio: "舞台剧出身，以克制细腻的表演风格著称。在《妈妈的职业电影》中，她用一个眼神完成了全片最重的一场戏，被观众称为“把沉默演成了台词”。",
    facts: [
      { label: "出道作品", value: "话剧《长夜》" },
      { label: "代表角色", value: "苏明娟" },
      { label: "表演风格", value: "克制 · 生活流" },
    ],
    films: [
      { id: "art-01", title: "《妈妈的职业电影》剧情全解析", cover: "https://readdy.ai/api/search-image?query=Cinematic%20film%20still%20of%20a%20woman%20working%20in%20a%20warm%20kitchen%20under%20morning%20light%2C%20muted%20orange%20and%20blue-gray%20palette%2C%20editorial%20photography%2C%20no%20text%2C%20warm%20mood&width=400&height=260&seq=momfilmactorfilm01&orientation=landscape", year: "2026" },
      { id: "art-06", title: "主题解读：职业与母职之间", cover: "https://readdy.ai/api/search-image?query=Cinematic%20film%20still%20of%20a%20woman%20between%20a%20doorway%20and%20a%20bright%20street%2C%20warm%20orange%20and%20cool%20blue-gray%20light%2C%20editorial%20photography%2C%20no%20text%2C%20reflective%20mood&width=400&height=260&seq=momfilmactorfilm02&orientation=landscape", year: "2026" },
      { id: "art-07", title: "经典台词盘点", cover: "https://readdy.ai/api/search-image?query=Cinematic%20film%20still%20of%20a%20quiet%20conversation%20in%20a%20dim%20room%20lit%20by%20a%20single%20lamp%2C%20blue-gray%20shadows%20with%20orange%20highlight%2C%20editorial%20photography%2C%20no%20text%2C%20emotional%20mood&width=400&height=260&seq=momfilmactorfilm03&orientation=landscape", year: "2026" },
    ],
  },
  {
    id: "gu-qinghe",
    name: "顾清和",
    role: "饰 苏念（女儿）",
    tagline: "追问的一代",
    avatar:
      "https://readdy.ai/api/search-image?query=Cinematic%20studio%20portrait%20of%20a%20young%20Chinese%20woman%20in%20her%20twenties%20with%20long%20straight%20hair%2C%20thoughtful%20gaze%2C%20warm%20neutral%20background%20with%20orange%20and%20blue-gray%20tones%2C%20soft%20lighting%2C%20editorial%20photography%2C%20no%20text&width=520&height=650&seq=momfilmactor02&orientation=portrait",
    bio: "新生代演员，擅长诠释内心戏丰富的都市青年角色。她在片中饰演长大的苏念，用一连串追问撑起了全片的另一条情感线。",
    facts: [
      { label: "出道作品", value: "网剧《晚风信箱》" },
      { label: "代表角色", value: "苏念" },
      { label: "表演风格", value: "灵动 · 细腻" },
    ],
    films: [
      { id: "art-04", title: "人物关系图谱", cover: "https://readdy.ai/api/search-image?query=Cinematic%20film%20still%20of%20a%20mother%20and%20daughter%20facing%20each%20other%20under%20warm%20window%20light%2C%20orange%20and%20blue-gray%20palette%2C%20editorial%20photography%2C%20no%20text%2C%20intimate%20mood&width=400&height=260&seq=momfilmactorfilm04&orientation=landscape", year: "2026" },
      { id: "art-03", title: "结局解读：那封信", cover: "https://readdy.ai/api/search-image?query=Cinematic%20film%20still%20of%20an%20old%20drawer%20with%20a%20folded%20letter%2C%20warm%20lamp%20light%20and%20deep%20blue-gray%20shadows%2C%20editorial%20photography%2C%20no%20text%2C%20tender%20mood&width=400&height=260&seq=momfilmactorfilm05&orientation=landscape", year: "2026" },
    ],
  },
  {
    id: "chen-ziang",
    name: "陈子昂",
    role: "饰 周叙",
    tagline: "旁观者的温度",
    avatar:
      "https://readdy.ai/api/search-image?query=Cinematic%20studio%20portrait%20of%20a%20calm%20Chinese%20man%20in%20his%20thirties%20with%20short%20hair%20and%20a%20gentle%20expression%2C%20warm%20neutral%20background%20with%20orange%20and%20blue-gray%20tones%2C%20soft%20lighting%2C%20editorial%20photography%2C%20no%20text&width=520&height=650&seq=momfilmactor03&orientation=portrait",
    bio: "以稳重的银幕形象受到关注。片中他饰演的周叙既是故事的见证者，也是推动苏明娟直面内心的重要人物。",
    facts: [
      { label: "出道作品", value: "电影《归途》" },
      { label: "代表角色", value: "周叙" },
      { label: "表演风格", value: "沉稳 · 内敛" },
    ],
    films: [
      { id: "art-10", title: "配乐解析", cover: "https://readdy.ai/api/search-image?query=Cinematic%20film%20still%20of%20a%20piano%20in%20a%20dim%20room%2C%20warm%20amber%20light%20and%20deep%20blue-gray%20shadows%2C%20editorial%20photography%2C%20no%20text%2C%20melancholic%20mood&width=400&height=260&seq=momfilmactorfilm06&orientation=landscape", year: "2026" },
      { id: "art-14", title: "拍摄地巡礼", cover: "https://readdy.ai/api/search-image?query=Cinematic%20film%20still%20of%20a%20quiet%20old%20street%20with%20plane%20trees%20at%20golden%20hour%2C%20warm%20orange%20and%20cool%20blue-gray%20palette%2C%20editorial%20photography%2C%20no%20text%2C%20nostalgic%20mood&width=400&height=260&seq=momfilmactorfilm07&orientation=landscape", year: "2026" },
    ],
  },
  {
    id: "zhou-zhiwei",
    name: "周知微",
    role: "饰 少年苏念",
    tagline: "最纯净的一束光",
    avatar:
      "https://readdy.ai/api/search-image?query=Cinematic%20studio%20portrait%20of%20a%20Chinese%20teenage%20girl%20with%20a%20ponytail%20and%20bright%20honest%20eyes%2C%20warm%20neutral%20background%20with%20orange%20and%20blue-gray%20tones%2C%20soft%20lighting%2C%20editorial%20photography%2C%20no%20text&width=520&height=650&seq=momfilmactor04&orientation=portrait",
    bio: "凭借自然不做作的表演被观众记住。她饰演的少年苏念，是整部影片中最明亮、也最令人心疼的一抹色彩。",
    facts: [
      { label: "出道作品", value: "短片《夏天的课桌》" },
      { label: "代表角色", value: "少年苏念" },
      { label: "表演风格", value: "自然 · 真挚" },
    ],
    films: [
      { id: "art-05", title: "开篇十分钟全解析", cover: "https://readdy.ai/api/search-image?query=Cinematic%20film%20still%20of%20a%20breakfast%20table%20with%20a%20folded%20school%20uniform%2C%20soft%20morning%20light%2C%20warm%20orange%20and%20blue-gray%20palette%2C%20editorial%20photography%2C%20no%20text%2C%20cozy%20mood&width=400&height=260&seq=momfilmactorfilm08&orientation=landscape", year: "2026" },
      { id: "art-02", title: "剧情主线梳理", cover: "https://readdy.ai/api/search-image?query=Cinematic%20film%20still%20of%20a%20hospital%20corridor%20at%20night%20with%20soft%20light%2C%20cool%20blue-gray%20tones%20with%20warm%20accents%2C%20editorial%20photography%2C%20no%20text%2C%20quiet%20mood&width=400&height=260&seq=momfilmactorfilm09&orientation=landscape", year: "2026" },
    ],
  },
  {
    id: "xu-yiran",
    name: "许亦然",
    role: "饰 陈医生",
    tagline: "职场里的那盏灯",
    avatar:
      "https://readdy.ai/api/search-image?query=Cinematic%20studio%20portrait%20of%20a%20kind%20Chinese%20woman%20in%20her%20forties%20with%20tidy%20hair%20and%20a%20warm%20smile%2C%20neutral%20background%20with%20orange%20and%20blue-gray%20tones%2C%20soft%20lighting%2C%20editorial%20photography%2C%20no%20text&width=520&height=650&seq=momfilmactor05&orientation=portrait",
    bio: "资深演员，饰演苏明娟在医院的引路人。她用不多却精准的戏份，为这个职场世界增添了温度。",
    facts: [
      { label: "出道作品", value: "电视剧《晨光诊所》" },
      { label: "代表角色", value: "陈医生" },
      { label: "表演风格", value: "干练 · 温和" },
    ],
    films: [
      { id: "art-06", title: "主题解读", cover: "https://readdy.ai/api/search-image?query=Cinematic%20film%20still%20of%20a%20woman%20between%20a%20doorway%20and%20a%20bright%20street%2C%20warm%20orange%20and%20cool%20blue-gray%20light%2C%20editorial%20photography%2C%20no%20text%2C%20reflective%20mood&width=400&height=260&seq=momfilmactorfilm10&orientation=landscape", year: "2026" },
      { id: "art-02", title: "剧情主线梳理", cover: "https://readdy.ai/api/search-image?query=Cinematic%20film%20still%20of%20a%20hospital%20corridor%20at%20night%20with%20soft%20light%2C%20cool%20blue-gray%20tones%20with%20warm%20accents%2C%20editorial%20photography%2C%20no%20text%2C%20quiet%20mood&width=400&height=260&seq=momfilmactorfilm11&orientation=landscape", year: "2026" },
    ],
  },
  {
    id: "shen-shubai",
    name: "沈书白",
    role: "饰 外婆",
    tagline: "三代人的记忆坐标",
    avatar:
      "https://readdy.ai/api/search-image?query=Cinematic%20studio%20portrait%20of%20an%20elderly%20Chinese%20woman%20with%20silver%20hair%20and%20a%20serene%20wise%20expression%2C%20warm%20neutral%20background%20with%20orange%20and%20blue-gray%20tones%2C%20soft%20lighting%2C%20editorial%20photography%2C%20no%20text&width=520&height=650&seq=momfilmactor06&orientation=portrait",
    bio: "老戏骨，戏份不多却场场精彩。她饰演的外婆串联起三代人的记忆，是影片中最安定的一个存在。",
    facts: [
      { label: "出道作品", value: "话剧《四季》" },
      { label: "代表角色", value: "外婆" },
      { label: "表演风格", value: "沉稳 · 厚重" },
    ],
    films: [
      { id: "art-09", title: "幕后花絮：厨房场景", cover: "https://readdy.ai/api/search-image?query=Behind%20the%20scenes%20film%20set%20of%20a%20warm%20domestic%20kitchen%20with%20props%2C%20muted%20orange%20and%20blue-gray%20tones%2C%20editorial%20photography%2C%20no%20text%2C%20crafted%20atmosphere&width=400&height=260&seq=momfilmactorfilm12&orientation=landscape", year: "2026" },
      { id: "art-13", title: "同类影片推荐", cover: "https://readdy.ai/api/search-image?query=Cinematic%20film%20still%20of%20a%20stack%20of%20movie%20posters%20and%20a%20warm%20cup%20of%20tea%2C%20soft%20window%20light%2C%20orange%20and%20blue-gray%20palette%2C%20editorial%20photography%2C%20no%20text%2C%20welcoming%20mood&width=400&height=260&seq=momfilmactorfilm13&orientation=landscape", year: "2026" },
    ],
  },
];

export const guideGroups: GuideGroup[] = [
  {
    key: "notice",
    label: "观影须知",
    intro: "第一次看这部片子之前，有几件小事能让你的观影体验更好。",
    items: [
      { title: "适合一个人安静地看", desc: "影片节奏舒缓、情绪后置，建议留出完整的两小时，不要边看边做别的事。", articleId: "art-01" },
      { title: "留意开头的十分钟", desc: "三个关键伏笔都藏在开篇的日常镜头里，看完结局再回看会有全新感受。", articleId: "art-05" },
      { title: "准备一点纸巾", desc: "不是煽情，而是那种“被生活击中”的安静落泪，很多人会在结尾处破防。", articleId: "art-03" },
    ],
  },
  {
    key: "timing",
    label: "推荐观影时机",
    intro: "不同的时间和状态，会看到不一样的《妈妈的职业电影》。",
    items: [
      { title: "周末的傍晚", desc: "暖色调的画面与傍晚的自然光最相配，看完刚好可以散个步消化情绪。", articleId: "art-06" },
      { title: "和家人一起看", desc: "如果条件允许，和母亲或孩子一起看，结束后你们大概会有很多话想说。", articleId: "art-04" },
      { title: "一个人加班后的深夜", desc: "如果你也曾在深夜感到疲惫，苏明娟那句“我只是不敢停”会格外有力量。", articleId: "art-07" },
    ],
  },
  {
    key: "review",
    label: "影评精选",
    intro: "来自观众与影评人的一些真实感受，角度各不相同。",
    items: [
      { title: "它没有歌颂母亲，它理解母亲", desc: "“影片最难得的地方，是把母亲当成一个普通人来拍摄，而不是一个符号。”", articleId: "art-06" },
      { title: "结构上的一次野心", desc: "“双线并行却毫不凌乱，导演对时间的控制力令人印象深刻。”", articleId: "art-08" },
      { title: "细节的胜利", desc: "“那些没有台词的镜头，比任何一段对白都更有说服力。”", articleId: "art-05" },
    ],
  },
  {
    key: "extend",
    label: "观影延伸",
    intro: "看完之后想了解更多？这些内容值得继续读下去。",
    items: [
      { title: "主题曲与配乐解析", desc: "了解那段反复出现的旋律，是如何为剧情服务的。", articleId: "art-10" },
      { title: "幕后与拍摄地巡礼", desc: "跟着镜头走进真实的街巷，看看那些场景是怎么来的。", articleId: "art-14" },
      { title: "五部关于母亲的电影", desc: "如果你喜欢这部片子，这份片单也许正合你意。", articleId: "art-13" },
    ],
  },
];